import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";

interface NetworkConnection {
  uuid: string;
  status: "Pending" | "Active" | "Revoked";
  direction: "buyer" | "supplier";
  counterpart: { uuid: string; name: string; slug: string };
  invitedAt: string;
  acceptedAt: string | null;
  revokedAt: string | null;
  revokeReason: string | null;
}

const formatDateUk = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

/**
 * Shows on the Dashboard when this org has incoming network invitations
 * (buyers asking to connect to us as a supplier). Only renders when there
 * is at least one pending invitation; silent otherwise.
 */
export function NetworkInvitationsBanner() {
  const { slug } = useParams<{ slug: string }>();
  const [pending, setPending] = useState<NetworkConnection[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await api<{ connections: NetworkConnection[] }>("/network/connections/pending");
      setPending(res.connections);
    } catch (err) {
      setError(formatApiError(err)[0] ?? "Failed to load network invitations");
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (error || !pending || pending.length === 0) return null;

  const accept = async (uuid: string) => {
    setBusy(true);
    try {
      await api(`/network/connections/${uuid}/accept`, { method: "POST" });
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const decline = async (uuid: string) => {
    setBusy(true);
    try {
      await api(`/network/connections/${uuid}/decline`, { method: "POST", body: {} });
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      role="region"
      aria-label="Network invitations"
      className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/40 dark:bg-amber-500/10"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-amber-900 dark:text-amber-100">
            {pending.length} buyer{pending.length === 1 ? "" : "s"} want to connect with you
          </h2>
          <p className="mt-1 text-xs text-amber-800 dark:text-amber-200">
            Accepting lets each buyer view your published authorised evidence. They cannot edit
            anything — they only see what you choose to publish.
          </p>
        </div>
        {slug ? (
          <Link
            to={`/o/${slug}/network`}
            className="rounded-md border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100 dark:border-amber-800 dark:bg-slate-900 dark:text-amber-100"
          >
            Manage all
          </Link>
        ) : null}
      </div>
      <ul className="mt-3 space-y-2">
        {pending.map((c) => (
          <li
            key={c.uuid}
            className="flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-white p-3 text-sm dark:border-amber-900/40 dark:bg-slate-800"
          >
            <div>
              <p className="font-medium text-slate-900 dark:text-slate-100">{c.counterpart.name}</p>
              <p className="text-xs text-slate-600 dark:text-slate-400">
                Invited {formatDateUk(c.invitedAt)} · slug:{" "}
                <span className="font-mono">{c.counterpart.slug}</span>
              </p>
            </div>
            <div className="flex shrink-0 gap-2">
              <button
                type="button"
                onClick={() => void accept(c.uuid)}
                disabled={busy}
                className="rounded-md bg-amber-600 px-3 py-1 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-60"
              >
                Accept
              </button>
              <button
                type="button"
                onClick={() => void decline(c.uuid)}
                disabled={busy}
                className="rounded-md border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
              >
                Decline
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
